import React, { useEffect, useState, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { Modal } from "react-bootstrap";
import Button from "../../ui/Button";

const SNOOZE_LABEL = "Stay signed in for an extra hour";
const MODAL_TITLE = "Session expiring soon";
const BUTTON_TEXT = "Stay signed in";

const formatTime = (seconds) => {
    const total = Math.max(0, Math.floor(seconds || 0));
    const minutes = Math.floor(total / 60);
    const remainingSeconds = total % 60;

    return `${minutes}:${String(remainingSeconds).padStart(2, "0")}`;
};

function SessionExpiryModal({
    show,
    remainingSeconds,
    onStaySignedIn,
    onSnooze,
}) {
    const [snoozeChecked, setSnoozeChecked] = useState(false);

    useEffect(() => {
        if (show) {
            setSnoozeChecked(false);
        }
    }, [show]);

    const handleConfirm = useCallback(() => {
        if (snoozeChecked) {
            onSnooze();
        } else {
            onStaySignedIn();
        }
    }, [snoozeChecked, onSnooze, onStaySignedIn]);

    const handleCheckboxChange = useCallback((e) => {
        setSnoozeChecked(e.target.checked);
    }, []);

    const formattedTime = useMemo(
        () => formatTime(remainingSeconds),
        [remainingSeconds]
    );

    const getTimerColor = () => {
        if (remainingSeconds <= 30) return "#dc2626"; // red-600
        if (remainingSeconds <= 60) return "#ea580c"; // orange-600
        return "#000000";
    };

    const timerStyle = {
        color: getTimerColor(),
    };

    return (
        <Modal
            show={show}
            onHide={() => {}}
            backdrop="static"
            keyboard={false}
            centered
        >
            <Modal.Header>
                <Modal.Title>{MODAL_TITLE}</Modal.Title>
            </Modal.Header>
            <Modal.Body className="flex flex-col items-center gap-4 p-6 text-center">
                <div>You will be signed out in</div>
                <div
                    className="text-4xl font-medium"
                    style={timerStyle}
                    aria-live="polite"
                    aria-atomic="true"
                >
                    {formattedTime}
                </div>
                <div className="w-3/5">
                    <Button
                        id="session-expiry-stay-signed-in"
                        onClick={handleConfirm}
                        text={BUTTON_TEXT}
                        customHeight="h-11"
                    />
                </div>
                <div className="flex justify-center">
                    <input
                        id="snooze-checkbox"
                        className="text-primary-500 mr-2 w-4 cursor-pointer"
                        type="checkbox"
                        checked={snoozeChecked}
                        onChange={handleCheckboxChange}
                        aria-label={SNOOZE_LABEL}
                    />
                    <label className="mb-0" htmlFor="snooze-checkbox">
                        {SNOOZE_LABEL}
                    </label>
                </div>
            </Modal.Body>
        </Modal>
    );
}

SessionExpiryModal.propTypes = {
    show: PropTypes.bool.isRequired,
    remainingSeconds: PropTypes.number.isRequired,
    onStaySignedIn: PropTypes.func.isRequired,
    onSnooze: PropTypes.func.isRequired,
};

export default SessionExpiryModal;
